<script setup lang="ts">
import type { ScaleConfig } from "@/types/legend.types";
import type { InputInstance } from "element-plus";
import { computed, ref } from "vue";
import ScaleConfigItem from "./ScaleConfigItem.vue";
import DragReorderList from "./DragReorderList.vue";
import DRBtn from "@/components/UI/DRBtn.vue";
import { defaultScale } from "./edit-legend.models";
import { useToast } from "@/composable/useToast";
import { editScaleName } from "./utils";
import { v4 } from "uuid";

const { showDanger } = useToast();

const props = defineProps<{
  modelValue: ScaleConfig[];
}>();

const emits = defineEmits<{
  (e: "update:modelValue", layers: ScaleConfig[]): void;
}>();

const scales = computed({
  get: () => props.modelValue,
  set: (value) => emits("update:modelValue", value),
});

const checkScaleNameExist = (name: string, updatingIndex: number): boolean => {
  return scales.value.some(
    (scale, i) => scale.name === name && i !== updatingIndex
  );
};

const newScaleRef = ref<InputInstance | null>(null);
const newScaleName = ref("");

const addScale = () => {
  const name = newScaleName.value?.trim();
  if (checkScaleNameExist(name, -1)) {
    showDanger("dashboard:alert.scale.already.exists");
    newScaleRef.value?.focus();
    return;
  }
  scales.value = [
    ...scales.value,
    { ...editScaleName(defaultScale, name), id: v4() },
  ];
  newScaleName.value = "";
  newScaleRef.value?.focus();
};

const updateScale = (index: number, value: ScaleConfig) => {
  const name = value.name?.trim();
  if (checkScaleNameExist(name, index)) {
    showDanger("dashboard:alert.scale.already.exists");
    newScaleRef.value?.focus();
    return;
  }
  scales.value = scales.value.map((scale, i) => (i === index ? value : scale));
};

const removeScale = (index: number) => {
  scales.value = scales.value.filter((_, i) => i !== index);
};

const isValid = computed(() => {
  return newScaleName.value?.trim().length === 0;
});
</script>

<template>
  <div class="relative-position">
    <DragReorderList
      :items="scales"
      group="legend-scales"
      @reorder="scales = $event"
    >
      <template #default="{ item: scale, index }">
        <ScaleConfigItem
          :model-value="scale"
          @update:modelValue="updateScale(index, $event)"
          @remove="removeScale(index)"
        />
      </template>
    </DragReorderList>
  </div>

  <div class="d-flex gap-3 pt-3">
    <el-input
      ref="newScaleRef"
      class="col-3"
      size="small"
      v-model="newScaleName"
      :placeholder="defaultScale.name"
      @keyup.enter="addScale()"
    />
    <DRBtn
      class="col-auto"
      variant="secondary"
      :label="$t('add')"
      :disabled="isValid"
      @click="addScale()"
    />
  </div>
</template>

<style lang="scss" scoped></style>
